/**
 * The dynamic values a name can carry, and how a name string splits into text
 * and tokens. A name is stored as plain text with tokens in braces
 * ("Sprint {date}"); lib/dynamicValues resolves them at apply, and
 * components/DynamicTextField shows them as pills while editing.
 *
 * Adding a value: one entry here (token, label, group, preview) and its
 * resolution in resolveName (lib/dynamicValues, mirrored in
 * spt/src/helpers/dynamic-values.ts).
 */
import { applyBaseDate, DATE_TOKEN, formatDate } from './dynamicValues';

/**
 * token        the text stored in the name
 * label        what the pill and the menu show
 * group        the menu section
 * description  the menu's hint for it
 * preview(ctx) today's value, for the menu and the preview line
 */
export const DYNAMIC_TOKENS = Object.freeze([
  {
    token: DATE_TOKEN,
    label: 'Apply date',
    group: 'Date',
    description: 'The day the template is applied, in your timezone',
    preview: (ctx) => formatDate(ctx.date),
  },
]);

export function tokenDef(token, tokens = DYNAMIC_TOKENS) {
  return tokens.find((t) => t.token === token) || null;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "Sprint {date}" → [{ kind: 'text', text: 'Sprint ' }, { kind: 'token', token: '{date}', def }].
 * Only known tokens become token segments; any other braces stay text.
 */
export function tokenSegments(value, tokens = DYNAMIC_TOKENS) {
  const s = String(value == null ? '' : value);
  if (!s) return [];
  if (!tokens.length) return [{ kind: 'text', text: s }];
  const re = new RegExp(tokens.map((t) => escapeRe(t.token)).join('|'), 'g');
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ kind: 'text', text: s.slice(last, m.index) });
    out.push({ kind: 'token', token: m[0], def: tokenDef(m[0], tokens) });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

/** The context previews resolve against: today in the viewer's timezone. */
export function previewContext(timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, now = new Date()) {
  return { date: applyBaseDate(timeZone, now) };
}

/** The menu's sections for a search query: [{ group, items: [def] }], in registry order. */
export function groupedTokens(query = '', tokens = DYNAMIC_TOKENS) {
  const q = query.trim().toLowerCase();
  const hits = tokens.filter(
    (t) => !q || t.label.toLowerCase().includes(q) || t.group.toLowerCase().includes(q) || t.token.toLowerCase().includes(q),
  );
  const groups = [];
  for (const t of hits) {
    let g = groups.find((x) => x.group === t.group);
    if (!g) groups.push((g = { group: t.group, items: [] }));
    g.items.push(t);
  }
  return groups;
}
